import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import { toListQuery } from './filters';
import { userDetailSchema, userListResponseSchema } from './schemas';
import type {
  CreateUserBody,
  UpdateUserBody,
  UserDetail,
  UserListParams,
  UserListResponse,
  UserStatus,
} from './types';

/** The only FE sort key the real backend spells differently (confirmed 2026-08-04): its
 *  `sortBy` enum is `uniqueId|name|role|email|phone|status|createdDate` — no `createdAt`. Every
 *  visible column's key already matches the enum directly (the User column sorts by `name`). */
const SORT_FIELD_MAP: Record<string, string> = { createdAt: 'createdDate' };

/** Sort + filters → the list's wire params, remap and defaults included. The export sends the
 *  same, so the file comes out with the rows and order on screen, sorted or not (ADO 120974). */
export function usersRequestQuery(params: Omit<UserListParams, 'page' | 'limit'>) {
  const { sortBy = 'createdAt', sortOrder = 'desc', ...filters } = params;
  return {
    sortBy: SORT_FIELD_MAP[sortBy] ?? sortBy,
    sortOrder: sortOrder.toUpperCase(),
    ...toListQuery(filters),
  };
}

/** Paginated/sortable/searchable Internal Users list. Default sort `createdAt desc` (D11) —
 *  mirrors payments' `paidAt` default; `createdAt` is a sort key only, not a visible column.
 *  `sortBy` is remapped and `sortOrder` uppercased at the wire boundary (mirrors `resource.ts`'s
 *  convention) since the backend's enum uses different key names and uppercase `ASC`/`DESC`. */
export async function listUsers(params: UserListParams): Promise<UserListResponse> {
  const { page, limit, ...rest } = params;
  const { data, meta } = await apiClient.getList(endpoints.adminUsers.list, {
    params: { page, limit, ...usersRequestQuery(rest) },
  });
  return userListResponseSchema.parse({ data, meta });
}

/** Full detail for one user (`GET .../:id`) — separate `firstName`/`lastName`, unlike the list row's
 *  combined `name`. Used to seed the Edit form, since the row clicked can't supply those directly. */
export async function getUser(id: string): Promise<UserDetail> {
  return userDetailSchema.parse(await apiClient.get(endpoints.adminUsers.detail(id)));
}

/** Onboard an internal user. 409 → `USER_PHONE_EXISTS` / `USER_EMAIL_EXISTS` propagate as
 *  `ApiError.code` to the drawer for inline field errors (D14). The response body is discarded —
 *  nothing in the app reads it (the list refreshes via invalidation), so it isn't schema-validated
 *  either; that would only add a chance to crash on a shape we don't otherwise depend on. */
export async function createUser(body: CreateUserBody): Promise<void> {
  await apiClient.post(endpoints.adminUsers.create, body);
}

/** Update an internal user via `PUT`, sending the full form (confirmed 2026-08-04: the real
 *  endpoint requires the complete `CreateInternalUserDto`, not a partial patch — this reverses the
 *  original "send only changed fields" design, D13). `diffUserForm` still guards the no-op case
 *  client-side (skip the request/toast when nothing genuinely changed), it just no longer supplies
 *  the request body. Same 409 uniqueness codes as create; they propagate as `ApiError.code` to the
 *  drawer for inline field errors (D14). Response body discarded, same reasoning as `createUser`. */
export async function updateUser(id: string, body: UpdateUserBody): Promise<void> {
  await apiClient.put(endpoints.adminUsers.detail(id), body);
}

/** Enable/Disable an internal user. 409 → `USER_HAS_ACTIVE_INSTALLATION_REQUEST` /
 *  `USER_HAS_ASSIGNED_SUPPORT_TICKET` (disable only) propagate as `ApiError.code` (D5). Response
 *  body discarded, same reasoning as `createUser`. */
export async function setUserStatus(id: string, status: UserStatus): Promise<void> {
  await apiClient.patch(endpoints.adminUsers.status(id), { status });
}

/** Delete an internal user (soft-delete server-side). 409 → the two dependency codes propagate as
 *  `ApiError.code` (D5). The `{ id }` body is unused — the list refreshes via invalidation. */
export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(endpoints.adminUsers.detail(id));
}
